import { queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNil, orderBy, sortBy } from 'lodash-es';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { isDeletedById } from '@/utils/check-messages-status';
import { fetcher } from '@/utils/fetcher';
import { avatarUrlSchema, nameSchema, stringifiedDateSchema } from '@/utils/schemas';
import { generateSessionKey } from '@/utils/session';

interface Options {
  type: ConversationType;
  id: number;
}

async function getSearchMessagesResult(
  keyword?: string,
  options?: Options,
  signal?: AbortSignal,
): Promise<SearchMessageResult[]> {
  const params = { Keyword: keyword };
  if (options?.type) {
    Object.assign(params, {
      [options.type === 'group' ? 'GroupID' : 'ChatSessionID']: options.id,
    });
  }

  const response = await fetcher<ListRespondedWith<SearchMessageResult>>('/GetChatSearchResult', params, {
    signal,
    schema: generateListResponseScheme(searchMessageResultSchema),
    validResponseCodes: [0, 1, 20],
  });

  if (!response?.Data) return [];

  let data = response.Data.map((item) => {
    const filteredDetails = sortBy(
      item.SearchResultDetails?.filter(
        (d) =>
          !isDeletedById(
            generateSessionKey(item.ChatSessionId ? 'session' : 'group', item.ChatSessionId || item.GroupId),
            d.Id,
          ),
      ),
      ({ Id }) => -Id,
    );

    return filteredDetails.length > 0 ? { ...item, SearchResultDetails: filteredDetails } : null;
  }).filter((v) => !isNil(v));

  data = orderBy(data, (d) => [d.SearchResultDetails[0]?.CreateTime ?? 0], ['desc']);

  return data;
}

const generateSearchMessagesQueryOptions = (keyword?: string, options?: Options) =>
  queryOptions({
    queryKey: ['/GetChatSearchResult', keyword, options ?? 'global'],
    queryFn: keyword ? ({ signal }) => getSearchMessagesResult(keyword, options, signal) : skipToken,
    staleTime: 10 * 1000,
    gcTime: 5 * 60 * 1000,
  });

export default function useSearchMessages(keyword = '', options?: Options) {
  const queryClient = useQueryClient();
  const [debouncedKeyword] = useDebounce(keyword, 600);
  const query = useQuery(generateSearchMessagesQueryOptions(debouncedKeyword, options));

  return {
    messages: query.data ?? [],
    clear: () => queryClient.resetQueries({ predicate: (query) => query.queryHash.includes('GetChatSearchResult') }),
    ...query,
  };
}

const searchMessageResultSchema = z.object({
  DisplayImg: avatarUrlSchema,
  SessionName: nameSchema,
  ChatSessionId: z.number(),
  GroupId: z.number(),
  SessionNameUserId: z.number(),
  SearchResultDetails: z.array(
    z.object({
      Id: z.number(),
      UserId: z.number(),
      HeaderImg: avatarUrlSchema,
      Message: z.string(),
      CreateTime: stringifiedDateSchema,
      SeqId: z.number(),
      NickName: nameSchema,
      MsgType: z.nativeEnum(ConversationMessageContentType),
    }),
  ),
});

export type SearchMessageResult = z.infer<typeof searchMessageResultSchema>;
